import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { OrgAdminApiError, updateOrganization, type Organization } from '@/api/orgAdmin';
import { useToast } from '@/components/ui';
import { OrgAvatar } from './OrgAvatar';
import {
    BRAND_SWATCHES,
    EMG_DEFAULT_SWATCH,
    contrastRatioOnWhite,
    findSwatchByHex,
    formatContrastRatioVi,
} from './orgBrandingSwatches';
import './OrgBrandingTab.css';

interface OrgBrandingTabProps {
    org: Organization;
    canManage: boolean;
}

interface BrandingFormState {
    hex: string;
    hoverHex: string;
    logoUrlText: string;
}

/**
 * EAA-1815: the org's current `primary_color` may be one of the curated
 * swatches, or a legacy free-typed hex from before this ticket (the editor
 * used to be a plain text input). Curated match wins; otherwise the legacy
 * value is kept as-is (shown as an unmatched "current color" chip) rather
 * than silently snapped to a swatch or discarded — the admin only loses the
 * free-text value once they deliberately pick a new swatch and Save.
 */
function getInitialFormState(org: Organization): BrandingFormState {
    const storedHex = org.branding?.primary_color;
    if (!storedHex) {
        return {
            hex: EMG_DEFAULT_SWATCH.hex,
            hoverHex: EMG_DEFAULT_SWATCH.hoverHex,
            logoUrlText: org.branding?.logo_url ?? '',
        };
    }
    const matched = findSwatchByHex(storedHex);
    return {
        hex: matched ? matched.hex : storedHex,
        hoverHex: matched ? matched.hoverHex : (org.branding?.primary_color_hover ?? storedHex),
        logoUrlText: org.branding?.logo_url ?? '',
    };
}

/**
 * EAA-1815: `/organizations/:id?tab=branding`, frame "26 branding-editor.png".
 *
 * Replaces the earlier free-hex-input version with the design's three
 * required pieces: a curated, contrast-safe color swatch set (no free picker
 * — see `orgBrandingSwatches.ts`), a drag-and-drop logo zone, and a live mock
 * of the Keycloak login card. The live preview consumes the SAME shape
 * `apps/sso/src/login/branding.ts`'s `OrgBranding` does (name, logo, primary
 * color/hover) so it never drifts from what the theme actually renders.
 *
 * Logo upload has a real backend gap: `portal_api.directory.org_serializers`
 * (`BRANDING_KEYS`) only stores `logo_url` as a string — there is no
 * multipart upload endpoint. The drop-zone still accepts a dropped/browsed
 * file for an instant client-side preview (object URL), but Save only ever
 * sends the URL text field; picking a file without pasting its hosted URL
 * shows an explanatory note instead of silently discarding the intent or
 * fabricating an upload call. See the drop-zone section below.
 */
export function OrgBrandingTab({ org, canManage }: OrgBrandingTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const initial = getInitialFormState(org);
    const [selectedHex, setSelectedHex] = useState(initial.hex);
    const [selectedHoverHex, setSelectedHoverHex] = useState(initial.hoverHex);
    const [logoUrlText, setLogoUrlText] = useState(initial.logoUrlText);
    const [localFile, setLocalFile] = useState<File | null>(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [fileNote, setFileNote] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    // Re-sync only when the ORG being edited changes (navigating between
    // organizations) — not on every refetch of the same org, which would
    // stomp an in-progress edit right after an unrelated invalidateQueries
    // call (e.g. this same mutation's own onSuccess).
    useEffect(() => {
        const next = getInitialFormState(org);
        setSelectedHex(next.hex);
        setSelectedHoverHex(next.hoverHex);
        setLogoUrlText(next.logoUrlText);
        setLocalFile(null);
        setFileNote(null);
        setFormError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org.id]);

    // Object URL for the locally-dropped/browsed file — preview-only, never
    // uploaded (see the doc comment above and the note rendered next to it).
    useEffect(() => {
        if (!localFile) {
            setLocalPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(localFile);
        setLocalPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [localFile]);

    const brandingMutation = useMutation({
        mutationFn: (branding: {
            logo_url?: string;
            primary_color?: string;
            primary_color_hover?: string;
        }) => updateOrganization(config.apiBase, accessToken as string, org.id, { branding }),
        onSuccess: () => {
            setFormError(null);
            toast.success(t('admin.organizations.branding.toastUpdated', { name: org.name }));
            void queryClient.invalidateQueries({ queryKey: ['organization', org.id] });
        },
        onError: (error) => {
            setFormError(
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('admin.organizations.branding.genericUpdateError')
            );
        },
    });

    const handleSelectSwatch = (hex: string, hoverHex: string) => {
        setSelectedHex(hex);
        setSelectedHoverHex(hoverHex);
    };
    const handleResetToDefault = () =>
        handleSelectSwatch(EMG_DEFAULT_SWATCH.hex, EMG_DEFAULT_SWATCH.hoverHex);

    const handleFiles = (fileList: FileList | null) => {
        const file = fileList?.[0];
        if (!file) return;
        if (!/^image\/(png|svg\+xml)$/i.test(file.type)) {
            setFileNote(t('admin.organizations.branding.fileTypeError'));
            return;
        }
        setFileNote(null);
        setLocalFile(file);
    };
    const handleBrowseClick = () => fileInputRef.current?.click();

    const handleCancel = () => {
        const reset = getInitialFormState(org);
        setSelectedHex(reset.hex);
        setSelectedHoverHex(reset.hoverHex);
        setLogoUrlText(reset.logoUrlText);
        setLocalFile(null);
        setFileNote(null);
        setFormError(null);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const branding: { logo_url?: string; primary_color?: string; primary_color_hover?: string } =
            {
                primary_color: selectedHex,
                primary_color_hover: selectedHoverHex,
            };
        if (logoUrlText.trim()) branding.logo_url = logoUrlText.trim();
        brandingMutation.mutate(branding);
    };

    const matchedSwatch = findSwatchByHex(selectedHex);
    const contrastRatio = contrastRatioOnWhite(selectedHex);
    const contrastOk = contrastRatio !== null && contrastRatio >= 4.5;
    const effectiveLogoUrl = localPreviewUrl ?? (logoUrlText.trim() || undefined);

    const previewCard = (
        <div className="portal-branding-preview-card">
            <div className="portal-branding-preview-identity">
                {effectiveLogoUrl ? (
                    <img src={effectiveLogoUrl} alt="" className="portal-branding-preview-logo" />
                ) : (
                    <OrgAvatar name={org.name} size={40} radius={8} background={selectedHex} color="#fff" />
                )}
                <span className="portal-branding-preview-name">{org.name}</span>
            </div>
            <span
                className="portal-branding-preview-button"
                style={{ '--brand-btn-bg': selectedHex } as CSSProperties}
                aria-hidden="true"
            >
                {t('admin.organizations.branding.previewButtonText', { name: org.name })}
            </span>
        </div>
    );

    if (!canManage) {
        return (
            <div className="portal-org-detail-main">
                <div className="portal-branding-editor">
                    <div className="portal-branding-editor-header">
                        <span className="portal-branding-editor-title">
                            {t('admin.organizations.branding.editorTitle', { name: org.name })}
                        </span>
                        <span className="portal-branding-editor-subtitle">
                            {t('admin.organizations.branding.readonlySubtitle')}
                        </span>
                    </div>

                    <div className="portal-branding-section">
                        <span className="portal-branding-section-label">
                            {t('admin.organizations.branding.primaryColorLabel')}
                        </span>
                        <span className="portal-branding-readonly-row">
                            <span
                                className="portal-branding-swatch-current-dot"
                                style={{ background: selectedHex }}
                                aria-hidden="true"
                            />
                            <code>{selectedHex.toUpperCase()}</code>
                        </span>
                    </div>

                    <div className="portal-branding-section">
                        <span className="portal-branding-section-label">
                            {t('admin.organizations.branding.logoLabel')}
                        </span>
                        <span className="portal-branding-readonly-row">
                            {org.branding?.logo_url ? (
                                <code>{org.branding.logo_url}</code>
                            ) : (
                                <span className="portal-muted">
                                    {t('admin.organizations.branding.noLogoLabel')}
                                </span>
                            )}
                        </span>
                    </div>

                    <div className="portal-branding-section">
                        <span className="portal-branding-preview-label">
                            {t('admin.organizations.branding.previewLabel')}
                        </span>
                        {previewCard}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="portal-org-detail-main">
            <div className="portal-branding-editor">
                <div className="portal-branding-editor-header">
                    <span className="portal-branding-editor-title">
                        {t('admin.organizations.branding.editorTitle', { name: org.name })}
                    </span>
                    <span className="portal-branding-editor-subtitle">
                        {t('admin.organizations.branding.editableSubtitle')}
                    </span>
                </div>

                <form id="branding-form" onSubmit={handleSubmit}>
                    <div className="portal-branding-form-sections">
                        <div className="portal-branding-section">
                            <span className="portal-branding-section-label">
                                {t('admin.organizations.branding.primaryColorLabel')}
                            </span>
                            <div className="portal-branding-swatch-row">
                                {BRAND_SWATCHES.map((swatch) => (
                                    <button
                                        key={swatch.key}
                                        type="button"
                                        className={
                                            'portal-branding-swatch' +
                                            (matchedSwatch?.key === swatch.key ? ' is-selected' : '')
                                        }
                                        style={{ background: swatch.hex }}
                                        title={swatch.label}
                                        aria-label={swatch.label}
                                        aria-pressed={matchedSwatch?.key === swatch.key}
                                        onClick={() => handleSelectSwatch(swatch.hex, swatch.hoverHex)}
                                    />
                                ))}
                                {!matchedSwatch && (
                                    <span
                                        className="portal-branding-swatch-current"
                                        title={t('admin.organizations.branding.currentColorTitle')}
                                    >
                                        <span
                                            className="portal-branding-swatch-current-dot"
                                            style={{ background: selectedHex }}
                                            aria-hidden="true"
                                        />
                                        {selectedHex.toUpperCase()}
                                    </span>
                                )}
                            </div>
                            {contrastRatio !== null && (
                                <p
                                    className={
                                        'portal-branding-contrast ' + (contrastOk ? 'is-ok' : 'is-warn')
                                    }
                                >
                                    {contrastOk ? '✓' : '⚠'}{' '}
                                    {t('admin.organizations.branding.contrastMessage', {
                                        ratio: formatContrastRatioVi(contrastRatio),
                                    })}{' '}
                                    —{' '}
                                    {contrastOk
                                        ? t('admin.organizations.branding.contrastOkSuffix')
                                        : t('admin.organizations.branding.contrastWarnSuffix')}
                                </p>
                            )}
                            <p className="portal-branding-hint">
                                {t('admin.organizations.branding.hintNoFreePicker')}
                            </p>
                        </div>

                        <div className="portal-branding-section">
                            <span className="portal-branding-section-label">
                                {t('admin.organizations.branding.logoLabel')}
                            </span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/svg+xml"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFiles(e.target.files)}
                            />
                            {/* Clickable div with role="button" + onKeyDown (Enter/Space) below
                                for keyboard parity — drag-and-drop needs a plain div/onDrop, a
                                real <button> can't be a drop target the same way. */}
                            <div
                                className={
                                    'portal-branding-dropzone' + (dragActive ? ' is-dragging' : '')
                                }
                                role="button"
                                tabIndex={0}
                                onClick={handleBrowseClick}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleBrowseClick();
                                    }
                                }}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragActive(true);
                                }}
                                onDragLeave={() => setDragActive(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragActive(false);
                                    handleFiles(e.dataTransfer.files);
                                }}
                            >
                                {effectiveLogoUrl ? (
                                    <img
                                        src={effectiveLogoUrl}
                                        alt=""
                                        className="portal-branding-dropzone-thumb"
                                    />
                                ) : (
                                    <OrgAvatar
                                        name={org.name}
                                        size={48}
                                        radius={8}
                                        background={selectedHex}
                                        color="#fff"
                                    />
                                )}
                                <span className="portal-branding-dropzone-copy">
                                    <span className="portal-branding-dropzone-title">
                                        {t('admin.organizations.branding.dropzoneTitle')}
                                    </span>
                                    <span className="portal-branding-dropzone-meta">
                                        {t('admin.organizations.branding.dropzoneMeta')}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    className="portal-btn portal-branding-dropzone-browse"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleBrowseClick();
                                    }}
                                >
                                    {t('admin.organizations.branding.chooseFileButton')}
                                </button>
                            </div>
                            {fileNote && <p className="portal-branding-file-note">{fileNote}</p>}
                            {/* EAA-1815: portal-api's branding field only stores `logo_url`
                                (BRANDING_KEYS in org_serializers.py) — there is no multipart
                                upload endpoint yet. A locally-picked file only ever previews
                                here; Save always sends the URL field below, never the file. */}
                            {localFile && (
                                <p className="portal-branding-hint">
                                    {t('admin.organizations.branding.localFileNote', {
                                        fileName: localFile.name,
                                    })}
                                </p>
                            )}
                            <label className="portal-branding-logo-url">
                                <span className="portal-branding-logo-url-label">
                                    {t('admin.organizations.branding.logoUrlLabel')}
                                </span>
                                <input
                                    value={logoUrlText}
                                    onChange={(e) => setLogoUrlText(e.target.value)}
                                    placeholder={t('admin.organizations.branding.logoUrlPlaceholder')}
                                />
                            </label>
                            <p className="portal-branding-hint">
                                {t('admin.organizations.branding.noLogoHint')}
                            </p>
                        </div>

                        <div className="portal-branding-section">
                            <span className="portal-branding-preview-label">
                                {t('admin.organizations.branding.previewLabel')}
                            </span>
                            {previewCard}
                        </div>
                    </div>
                </form>

                {formError && <p className="portal-admin-error">{formError}</p>}

                <div className="portal-branding-editor-footer">
                    <button
                        type="button"
                        className="portal-branding-reset-link"
                        onClick={handleResetToDefault}
                    >
                        {t('admin.organizations.branding.resetToDefaultLink')}
                    </button>
                    <div className="portal-branding-editor-actions">
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={handleCancel}
                            disabled={brandingMutation.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            form="branding-form"
                            className="portal-btn portal-btn-primary"
                            disabled={brandingMutation.isPending}
                        >
                            {brandingMutation.isPending
                                ? t('admin.organizations.branding.saveButtonPending')
                                : t('common.save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrgBrandingTab;
